// SlashMenu — the `/` popup, a pure `role="listbox"` over Shape-projected rows.
//
// Promoted from sketches/editor/SlashMenu.tsx (document-first-editor.md §1.3,
// §2.1) — same component, live location. Presentational ONLY: it holds no
// query, no open/close, no keyboard state — the combobox host (the block being
// typed into) owns all of that and drives selection through the
// aria-activedescendant pattern. The menu just paints the (already filtered)
// items and reports a click up. This is the "solved" a11y pattern the design
// picks OVER a document-wide contenteditable (§1.5): a listbox, not a textbox.
//
// Two looks are offered for the owner to choose between (§ "variant"): `labelled`
// (icon + name + one-line hint, the discoverability channel) and `compact`
// (icon + name only, a denser expert list). Same rows, same projection — only
// the row density differs.

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import type { SlashItem } from "./slashItems.js";
import "./SlashMenu.css";

export type SlashMenuVariant = "labelled" | "compact";

export type SlashMenuProps = {
    // The rows to show — already Shape-projected AND query-filtered by the host,
    // so the menu never decides what appears (single source stays upstream).
    readonly items: readonly SlashItem[];
    // Which row the keyboard cursor is on (index into `items`). The host clamps
    // it to the filtered length; -1 / out-of-range ⇒ nothing highlighted.
    readonly activeIndex: number;
    readonly variant: SlashMenuVariant;
    // aria plumbing for the combobox host: the listbox's id (host's
    // aria-controls) and a stable per-row id (host's aria-activedescendant).
    readonly listboxId: string;
    readonly optionId: (index: number) => string;
    // A row was chosen (click). Keyboard Enter is the host's job — it already
    // knows the active index. Both land on the same typed InsertIntent.
    readonly onCommit: (item: SlashItem) => void;
    // Pointer moved onto a row — the host syncs its keyboard cursor to it so
    // mouse and keyboard share one highlight (no rivalrous double cursor).
    readonly onHoverIndex?: (index: number) => void;
};

// The empty state — the query matched nothing in this template's Shape. A quiet
// row, role="presentation" so it is not announced as a selectable option.
const NoMatch = (): ReactNode => (
    <div className="bilda-slash__empty" role="presentation">
        <Text as="span" size="sm" tone="faint">
            <Trans>No blocks match</Trans>
        </Text>
    </div>
);

// A div-based listbox (not <ul>/<li>): the WAI-ARIA combobox popup owns its
// roles explicitly, and generic <div>s carry role="option" without the
// non-interactive-element lint that <li role="option"> trips. Options are NOT
// focus targets — the host's input keeps focus and drives selection through
// aria-activedescendant — so no tabindex is needed (and quick-nav elsewhere in
// the page is untouched).
export const SlashMenu = ({
    items,
    activeIndex,
    variant,
    listboxId,
    optionId,
    onCommit,
    onHoverIndex,
}: SlashMenuProps): ReactNode => {
    const { t } = useLingui();
    return (
        <div
            id={listboxId}
            role="listbox"
            aria-label={t`Insert block`}
            className={`bilda-slash bilda-slash--${variant}`}
        >
            {items.length === 0 ? <NoMatch /> : null}
            {items.map((item, index) => (
                <div
                    // The intent's discriminant + kind uniquely names a row (mirrors
                    // Palette's key), so the list is stable across re-filters.
                    key={`${item.intent.type}:${item.intent.kind}`}
                    id={optionId(index)}
                    role="option"
                    // Programmatically focusable (satisfies the interactive-role
                    // a11y floor) but not a tab stop: the host input keeps DOM focus
                    // and points here via aria-activedescendant (same as
                    // CommandPalette's option rows).
                    tabIndex={-1}
                    aria-selected={index === activeIndex}
                    className="bilda-slash__option"
                    // Commit on mousedown, not click: the host's input must not lose
                    // focus (and close the menu) before the pick registers.
                    onMouseDown={(event) => {
                        event.preventDefault();
                        onCommit(item);
                    }}
                    onMouseEnter={() => onHoverIndex?.(index)}
                >
                    <span className="bilda-slash__icon" aria-hidden="true">
                        <Icon name={item.icon} size="sm" />
                    </span>
                    <span className="bilda-slash__body">
                        <Text as="span" size="sm" weight="medium">
                            {item.label}
                        </Text>
                        {variant === "labelled" ? (
                            // dim, not faint: the keyboard-cursor row (aria-selected)
                            // tints the elevated background, and faint only clears
                            // AA on the untinted canvas (see Text.css).
                            <Text as="span" size="xs" tone="dim">
                                {item.hint}
                            </Text>
                        ) : null}
                    </span>
                </div>
            ))}
        </div>
    );
};
